import {Component, Input, OnInit} from '@angular/core';
import {
    BaseStepperFormComponent,
    CompDefinition,
    CompDefinitionAvailabilities,
    CompDefinitionService,
    CompDefinitionTypes,
    LoadingService,
    MembershipProgram,
    MenuItemPriceSummary,
    Organization
} from 'brewbill-lib';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModalController} from '@ionic/angular';

@Component({
    selector: 'bb-edit-comp-definition',
    templateUrl: './edit-comp-definition.component.html',
    styleUrls: ['./edit-comp-definition.component.scss'],
})
export class EditCompDefinitionComponent extends BaseStepperFormComponent implements OnInit {
    @Input() membershipProgram: MembershipProgram;
    @Input() compDefinition: CompDefinition;
    @Input() organization: Organization;
    formGroup: FormGroup;
    partial = true;

    priceSummaries: MenuItemPriceSummary[];
    filteredItems = [];

    compDefinitionTypes = CompDefinitionTypes;
    compDefinitionAvailabilities = CompDefinitionAvailabilities;

    constructor(
        modalController: ModalController,
        private compDefinitionService: CompDefinitionService,
        private loadingService: LoadingService,
        private formBuilder: FormBuilder
    ) {
        super(modalController);
    }

    ngOnInit() {
        const roles = [];
        if (!!this.compDefinition && !!this.compDefinition.roles) {
            this.compDefinition.roles.forEach(r => {
                const found = this.organization.roles.find(orgRole => orgRole.id === r.id);
                if (!!found) {
                    roles.push(found);
                }
            });
        }

        const membershipPrograms = [];
        if (!!this.compDefinition && !!this.compDefinition.membershipPrograms) {
            this.compDefinition.membershipPrograms.forEach(r => {
                const found = this.organization.membershipPrograms.find(p => p.id === r.id);
                if (!!found) {
                    membershipPrograms.push(found);
                }
            });
        }

        const organizationEvents = [];
        if (!!this.compDefinition && !!this.compDefinition.organizationEvents) {
            this.compDefinition.organizationEvents.forEach(r => {
                const found = this.organization.events.find(e => e.id === r.id);
                if (!!found) {
                    organizationEvents.push(found);
                }
            });
        }

        const requiredTabNote = !!this.compDefinition && !!this.compDefinition.requiredTabNote
            ? this.organization.tabNoteDefinitions.find(n => n.id === this.compDefinition.requiredTabNote.id)
            : '';

        const groupQualifiedItems = !this.compDefinition || (!!this.compDefinition.conditionItems && !!this.compDefinition.items
            && this.compDefinition.conditionItems.length === this.compDefinition.items.length
            && !this.compDefinition.conditionItems.some(c =>
                !this.compDefinition.items.some(q => q.itemId === c.itemId && q.priceId === c.priceId)));

        this.formGroup = this.formBuilder.group({
            id: !!this.compDefinition ? this.compDefinition.id : null,
            parentId: this.organization.id,
            name: [
                !!this.compDefinition ? this.compDefinition.name : (!!this.membershipProgram ? this.membershipProgram.name : ''),
                Validators.required
            ],
            color: [!!this.compDefinition ? this.compDefinition.color : '#fddb31', Validators.required],
            contrastColor: [!!this.compDefinition ? this.compDefinition.contrastColor : '#000000', Validators.required],
            description: !!this.compDefinition ? this.compDefinition.description : '',
            type: [!!this.compDefinition ? this.compDefinition.type : CompDefinitionTypes.PERCENT, Validators.required],
            availability: [
                !!this.compDefinition ? this.compDefinition.availability : (!this.membershipProgram
                    ? CompDefinitionAvailabilities.TAB
                    : CompDefinitionAvailabilities.ORDER_ITEM), Validators.required
            ],
            membershipProgram: this.membershipProgram,
            amount: [!!this.compDefinition ? this.compDefinition.amount : '', Validators.required],
            max: !!this.compDefinition ? this.compDefinition.max : '',
            roles: [roles],
            membershipPrograms: [membershipPrograms],
            birthday: !!this.compDefinition ? this.compDefinition.birthday : false,
            birthdayDuration: !!this.compDefinition ? this.compDefinition.birthdayDuration : 'DAY',
            organizationEvents: [organizationEvents],
            autoApply: !!this.compDefinition ? this.compDefinition.autoApply : false,
            requiredCount: !!this.compDefinition ? this.compDefinition.requiredCount : '',
            rewardCount: !!this.compDefinition ? this.compDefinition.rewardCount : '',
            priority: !!this.compDefinition ? this.compDefinition.priority : 'FIRST',
            limitDurationPart: !!this.compDefinition ? this.compDefinition.limitDurationPart : 'NONE',
            limitDurationCount: !!this.compDefinition ? this.compDefinition.limitDurationCount : 1,
            limitCount: !!this.compDefinition ? this.compDefinition.limitCount : '',
            visitCount: !!this.compDefinition ? this.compDefinition.visitCount : '',
            combinedCount: !!this.compDefinition ? this.compDefinition.combinedCount : null,
            sortOrder: !!this.compDefinition ? this.compDefinition.sortOrder : null,
            groupQualifiedItems,
            requiredTabNote
        });

        this.typeChanged();
        this.autoApplyChanged();
        this.durationChanged();
        this.rewardCountChanged();
        this.requiredCountChanged();

        this.priceSummaries = this.priceSummaries.map(item => {
            const container = new MenuItemPriceSummary({...item});
            container.qualified = !!this.compDefinition && !!this.compDefinition.items
                && this.compDefinition.items.some(q => q.itemId === item.itemId && q.priceId === item.priceId);

            container.condition = !!this.compDefinition && !!this.compDefinition.conditionItems
                && this.compDefinition.conditionItems.some(q => q.itemId === item.itemId && q.priceId === item.priceId);

            return container;
        });
    }

    filterItems(items: any[], filter: string) {
        const lowercaseFilter = !!filter ? filter.toLowerCase() : '';
        return !!filter ? items.filter((p: any) =>
                (!!p.itemName && p.itemName.toLowerCase().includes(lowercaseFilter))
                || (!!p.priceName && p.priceName.toLowerCase().includes(lowercaseFilter)))
            : items;
    }

    availabilityChanged() {
        if (this.formGroup.get('availability').value === CompDefinitionAvailabilities.TAB
            && this.formGroup.get('limitDurationPart').value === 'TAB') {
            this.formGroup.get('limitDurationPart').setValue(null);
        }
    }

    typeChanged() {
        if (this.formGroup.get('type').value === CompDefinitionTypes.MANUAL) {
            this.formGroup.get('amount').setValidators([]);
            this.formGroup.get('combinedCount').setValidators([]);
        } else if (this.formGroup.get('type').value === CompDefinitionTypes.COMBINED_PRICE) {
            this.formGroup.get('amount').setValidators([Validators.required]);
            this.formGroup.get('combinedCount').setValidators([Validators.required]);
            this.formGroup.get('availability').setValue(CompDefinitionAvailabilities.ORDER_ITEM);
        } else {
            this.formGroup.get('max').setValidators([]);
            this.formGroup.get('combinedCount').setValidators([]);
            if (this.formGroup.get('type').value === CompDefinitionTypes.PERCENT) {
                this.formGroup.get('amount').setValidators([Validators.required, Validators.max(100)]);
            } else {
                this.formGroup.get('amount').setValidators([Validators.required, Validators.max(99999)]);
            }
        }
        this.formGroup.controls.max.updateValueAndValidity();
        this.formGroup.controls.amount.updateValueAndValidity();
        this.formGroup.controls.combinedCount.updateValueAndValidity();
    }

    autoApplyChanged() {
        if (this.formGroup.get('autoApply').value) {
            this.formGroup.get('roles').setValue([]);
        }
    }

    birthdayChanged() {
        if (this.formGroup.get('birthday').value) {
            this.formGroup.get('limitDurationCount').setValue(1);
            this.formGroup.get('limitDurationPart').setValue('DAY');
            this.formGroup.controls.limitDurationPart.updateValueAndValidity();
        }
        this.durationChanged();
    }

    durationChanged() {
        if (this.formGroup.get('limitDurationPart').value === 'NONE') {
            this.formGroup.get('limitDurationCount').setValue(null);
            this.formGroup.get('limitDurationCount').setValidators([]);
            this.formGroup.get('limitCount').setValue(null);
            this.formGroup.get('limitCount').setValidators([]);
        } else {
            if (!this.formGroup.get('limitCount').value) {
                this.formGroup.get('limitCount').setValue(1);
            }

            if (!this.formGroup.get('limitDurationCount').value) {
                this.formGroup.get('limitDurationCount').setValue(1);
            }

            this.formGroup.get('limitCount').setValidators([Validators.required, Validators.min(1)]);

            if (this.formGroup.get('limitDurationPart').value === 'TAB') {
                this.formGroup.get('limitDurationCount').setValue(null);
                this.formGroup.get('limitDurationCount').setValidators([]);
            } else {
                this.formGroup.get('limitDurationCount').setValidators([Validators.required, Validators.min(1)]);
            }
        }

        this.formGroup.get('limitCount').updateValueAndValidity();
        this.formGroup.get('limitDurationPart').updateValueAndValidity();
        this.formGroup.get('limitDurationCount').updateValueAndValidity();
    }

    visitCountChanged() {
        if (this.formGroup.get('visitCount').value > 0) {
            if (!!this.formGroup.get('visitCount').value
                && (this.formGroup.get('limitDurationPart').value === 'TAB'
                    || this.formGroup.get('limitDurationPart').value === 'NONE')) {
                this.formGroup.get('limitDurationPart').setValue('EVER');
            }
        }
        this.formGroup.controls.limitDurationPart.updateValueAndValidity();
    }

    requiredCountChanged() {
        if (this.formGroup.get('requiredCount').value > 0) {
            if (!!this.formGroup.get('limitDurationPart').value && this.formGroup.get('limitDurationPart').value !== 'TAB') {
                this.formGroup.get('limitDurationPart').setValue('NONE');
            }
        }
        this.formGroup.controls.limitDurationPart.updateValueAndValidity();
    }

    rewardCountChanged() {
        if (this.formGroup.get('rewardCount').value > 0) {
            this.formGroup.get('requiredCount').setValidators([Validators.required, Validators.min(1)]);
        } else {
            this.formGroup.get('requiredCount').setValidators([]);
        }
        this.formGroup.controls.requiredCount.updateValueAndValidity();
    }

    onSubmit() {
        if (this.formGroup.valid) {
            const compDefinition = new CompDefinition(this.formGroup.value);
            if (!compDefinition.requiredTabNote) {
                compDefinition.requiredTabNote = null;
            }

            if (!compDefinition.membershipPrograms || compDefinition.membershipPrograms.length === 0) {
                compDefinition.visitCount = null;
            }

            compDefinition.items = compDefinition.availability === CompDefinitionAvailabilities.ORDER_ITEM ?
                this.priceSummaries.filter(item => item.qualified) : [];

            compDefinition.conditionItems = !this.formGroup.get('groupQualifiedItems').value ?
                (compDefinition.availability === CompDefinitionAvailabilities.ORDER_ITEM ?
                    this.priceSummaries.filter(item => item.condition) : [])
                : compDefinition.items;

            this.loadingService.present();
            if (!compDefinition.id) {
                this.compDefinitionService.create(compDefinition).subscribe(c => {
                    this.loadingService.dismiss();
                    this.close(new CompDefinition(c));
                });
            } else {
                this.compDefinitionService.update(compDefinition).subscribe(c => {
                    this.loadingService.dismiss();
                    this.close(new CompDefinition(c));
                });
            }
        }
    }

    setValue(field, val) {
        this.formGroup.get(field).setValue(val);
    }

    compClick(event) {
        console.log(event);
    }
}
